import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/communityView';

/** Query/mutation hooks for the inside-community view. Messages poll on an
 *  interval as a lightweight stand-in until the realtime chat pass wires the
 *  `cm:<id>` channel. */

export function useMessages(communityId: string) {
  return useQuery({
    queryKey: ['cv', 'messages', communityId],
    queryFn: () => api.listMessages(communityId),
    enabled: Boolean(communityId),
    // Realtime (useCommunityRealtime) drives live updates; this is a slow fallback.
    refetchInterval: 30000,
  });
}

export function useMembers(communityId: string, ownerId: string | null) {
  return useQuery({
    queryKey: ['cv', 'members', communityId],
    queryFn: () => api.listMembers(communityId, ownerId),
    enabled: Boolean(communityId),
  });
}

export function useMembership(communityId: string, userId: string | undefined) {
  return useQuery({
    queryKey: ['cv', 'membership', communityId, userId],
    queryFn: () => api.getMembership(communityId, userId as string),
    enabled: Boolean(communityId) && Boolean(userId),
  });
}

export function useLiveRooms(communityId: string) {
  return useQuery({
    queryKey: ['cv', 'rooms', communityId],
    queryFn: () => api.listLiveRooms(communityId),
    enabled: Boolean(communityId),
  });
}

/** Server-side reactions for the currently loaded messages. Re-keyed on the
 *  message set so new messages pull their reactions; realtime + toggles
 *  invalidate the `['cv','reactions',communityId]` prefix to refresh. */
export function useReactions(communityId: string, messageIds: string[]) {
  return useQuery({
    queryKey: ['cv', 'reactions', communityId, messageIds.length, messageIds[messageIds.length - 1] ?? ''],
    queryFn: () => api.listReactions(messageIds),
    enabled: Boolean(communityId) && messageIds.length > 0,
    refetchInterval: 30000,
  });
}

export function useToggleReaction(communityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, userId, emoji }: { messageId: string; userId: string; emoji: string }) =>
      api.toggleReaction(messageId, userId, emoji),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cv', 'reactions', communityId] }),
  });
}

export function useSendMessage(communityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.sendMessage,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cv', 'messages', communityId] }),
  });
}

export function useEditMessage(communityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => api.editMessage(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cv', 'messages', communityId] }),
  });
}

export function useDeleteMessage(communityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteMessage(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cv', 'messages', communityId] }),
  });
}

/** Invalidate everything that changes when membership flips. */
export function useInvalidateMembership(communityId: string) {
  const qc = useQueryClient();
  return (userId: string | undefined) => {
    qc.invalidateQueries({ queryKey: ['cv', 'membership', communityId, userId] });
    qc.invalidateQueries({ queryKey: ['cv', 'members', communityId] });
  };
}
